import { Controller } from "@hotwired/stimulus"

// Drives the AI outreach agent panel: lead search, running the agent in one of
// four modes, and committing the actions the agent proposes. The agent only
// proposes — the commit button is the single write path.
export default class extends Controller {
  static targets = [
    "searchInput", "searchResults", "selectedLead",
    "mode", "draftFields", "suggestFields", "eventFields",
    "channel", "goal", "customGoal", "customGoalWrapper", "tone",
    "suggestMode", "eventType", "eventPayload", "userNotes",
    "draft", "runButton", "results", "error"
  ]

  static values = {
    searchUrl: String,
    draftUrl: String,
    suggestUrl: String,
    assessUrl: String,
    eventUrl: String,
    commitUrl: String,
    lead: String
  }

  connect() {
    this.lead = null
    this.proposedActions = []
    this.searchTimer = null

    if (this.hasLeadValue && this.leadValue) {
      try {
        this.selectLead(JSON.parse(this.leadValue))
      } catch (_error) {
        // no preselected lead
      }
    }

    this.modeChanged()
    this.outsideClick = (event) => {
      if (!this.element.contains(event.target)) this.hideSearchResults()
    }
    document.addEventListener("click", this.outsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClick)
    if (this.searchTimer) clearTimeout(this.searchTimer)
  }

  // --- lead picker -------------------------------------------------------

  search() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this.performSearch(), 250)
  }

  async performSearch() {
    const query = this.searchInputTarget.value.trim()

    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error("Lead search failed")

      const leads = await response.json()
      this.renderSearchResults(leads)
    } catch (error) {
      this.showError(error.message)
    }
  }

  renderSearchResults(leads) {
    const container = this.searchResultsTarget
    container.innerHTML = ""

    if (!leads.length) {
      container.innerHTML = '<div class="px-3 py-2 text-sm text-gray-500">No matching prospects.</div>'
      container.classList.remove("hidden")
      return
    }

    leads.forEach((lead) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
      button.innerHTML = `<span class="font-medium text-gray-900"></span>
        <span class="text-xs text-gray-500 ml-1"></span>`
      button.querySelector(".font-medium").textContent = lead.name
      button.querySelector(".text-xs").textContent =
        [lead.organization, lead.status].filter(Boolean).join(" · ")
      button.addEventListener("click", () => {
        this.selectLead(lead)
        this.hideSearchResults()
      })
      container.appendChild(button)
    })

    container.classList.remove("hidden")
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add("hidden")
  }

  selectLead(lead) {
    this.lead = lead
    const details = [lead.organization, lead.status, lead.aiScore ? `score ${lead.aiScore}` : null]
      .filter(Boolean).join(" · ")
    this.selectedLeadTarget.innerHTML = `<span class="font-medium text-gray-900"></span>
      <span class="block text-xs text-gray-500"></span>`
    this.selectedLeadTarget.querySelector(".font-medium").textContent = lead.name
    this.selectedLeadTarget.querySelector(".text-xs").textContent = details
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
  }

  // --- mode switching ----------------------------------------------------

  modeChanged() {
    const mode = this.modeTarget.value
    this.draftFieldsTarget.classList.toggle("hidden", mode !== "draft")
    this.suggestFieldsTarget.classList.toggle("hidden", mode !== "suggest")
    this.eventFieldsTarget.classList.toggle("hidden", mode !== "event")
  }

  goalChanged() {
    this.customGoalWrapperTarget.classList.toggle("hidden", this.goalTarget.value !== "custom")
  }

  // --- running the agent -------------------------------------------------

  async run() {
    const mode = this.modeTarget.value
    if (mode !== "event" && !this.lead) {
      this.showError("Select a lead first.")
      return
    }
    if (mode === "suggest" && !this.draftTarget.value.trim()) {
      this.showError("Write or generate a draft before asking for suggestions.")
      return
    }

    this.clearError()
    this.setLoading(true)
    this.resultsTarget.innerHTML = '<div class="text-sm text-gray-500">Running agent…</div>'

    const requests = {
      draft: [this.draftUrlValue, {
        leadId: this.lead?.id,
        channel: this.channelTarget.value,
        goal: this.goalTarget.value,
        customGoal: this.customGoalTarget.value,
        tone: this.toneTarget.value,
        userNotes: this.userNotesTarget.value
      }],
      suggest: [this.suggestUrlValue, {
        leadId: this.lead?.id,
        currentDraft: this.draftTarget.value,
        mode: this.suggestModeTarget.value
      }],
      assess: [this.assessUrlValue, {
        leadId: this.lead?.id,
        userNotes: this.userNotesTarget.value
      }],
      event: [this.eventUrlValue, {
        leadId: this.lead?.id,
        eventType: this.eventTypeTarget.value,
        payload: this.eventPayloadTarget.value,
        userNotes: this.userNotesTarget.value
      }]
    }

    const [url, body] = requests[mode]

    try {
      const data = await this.postJson(url, body)
      this.renderResult(mode, data)
    } catch (error) {
      this.resultsTarget.innerHTML = ""
      this.showError(error.message)
    } finally {
      this.setLoading(false)
    }
  }

  async postJson(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      credentials: "same-origin",
      body: JSON.stringify(body)
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`)
    return data
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // --- rendering ---------------------------------------------------------

  renderResult(mode, data) {
    this.resultsTarget.innerHTML = ""
    this.proposedActions = data.proposedActions || []

    if (mode === "draft" && data.draft) {
      this.draftTarget.value = data.draft
      this.resultsTarget.appendChild(this.list("Subject lines", data.subjectLines))
      this.resultsTarget.appendChild(this.list("Personalization hooks", data.personalizationHooks))
      this.resultsTarget.appendChild(this.list("Talking points", data.talkingPoints))
    }

    if (mode === "suggest") {
      this.resultsTarget.appendChild(this.suggestionCards(data.suggestions || []))
    }

    if (mode === "assess") {
      this.resultsTarget.appendChild(this.card("Assessment", [
        `Priority: ${data.priority} · Score: ${data.score}/100`,
        data.fitRationale,
        data.recommendedNextStep ? `Next step: ${data.recommendedNextStep}` : null
      ].filter(Boolean).join("\n\n")))
      this.resultsTarget.appendChild(this.list("Strongest evidence", data.strongestEvidence))
      this.resultsTarget.appendChild(this.list("Outreach angles", data.outreachAngles))
      this.resultsTarget.appendChild(this.list("Risks or unknowns", data.risksOrUnknowns))
    }

    if (mode === "event") {
      this.resultsTarget.appendChild(this.card("Summary", data.summary))
      this.resultsTarget.appendChild(this.card("Recommendation", data.recommendation))
    }

    this.resultsTarget.appendChild(this.list("Risk flags", data.riskFlags))

    if (this.proposedActions.length) {
      this.resultsTarget.appendChild(this.actionsPanel(this.proposedActions))
    }
  }

  card(title, body) {
    const wrapper = document.createElement("div")
    if (!body) return wrapper

    wrapper.className = "bg-white rounded-lg border border-gray-200 p-4"
    wrapper.innerHTML = `<h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2"></h3>
      <p class="text-sm text-gray-800 whitespace-pre-wrap"></p>`
    wrapper.querySelector("h3").textContent = title
    wrapper.querySelector("p").textContent = body
    return wrapper
  }

  list(title, items) {
    const wrapper = document.createElement("div")
    if (!items || !items.length) return wrapper

    wrapper.className = "bg-white rounded-lg border border-gray-200 p-4"
    const heading = document.createElement("h3")
    heading.className = "text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2"
    heading.textContent = title
    const list = document.createElement("ul")
    list.className = "list-disc list-inside space-y-1 text-sm text-gray-800"
    items.forEach((item) => {
      const li = document.createElement("li")
      li.textContent = item
      list.appendChild(li)
    })
    wrapper.append(heading, list)
    return wrapper
  }

  suggestionCards(suggestions) {
    const wrapper = document.createElement("div")
    wrapper.className = "space-y-3"

    suggestions.forEach((suggestion) => {
      const card = document.createElement("div")
      card.className = "bg-white rounded-lg border border-gray-200 p-4"
      const text = document.createElement("p")
      text.className = "text-sm text-gray-800 whitespace-pre-wrap"
      text.textContent = suggestion.text
      const reason = document.createElement("p")
      reason.className = "text-xs text-gray-500 mt-2"
      reason.textContent = suggestion.reason
      const apply = document.createElement("button")
      apply.type = "button"
      apply.className = "mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
      apply.textContent = "Use this"
      apply.addEventListener("click", () => {
        const mode = this.suggestModeTarget.value
        this.draftTarget.value = mode === "complete"
          ? `${this.draftTarget.value}${suggestion.text}`
          : suggestion.text
      })
      card.append(text, reason, apply)
      wrapper.appendChild(card)
    })

    return wrapper
  }

  actionsPanel(actions) {
    const wrapper = document.createElement("div")
    wrapper.className = "bg-white rounded-lg border border-indigo-200 p-4"

    const heading = document.createElement("h3")
    heading.className = "text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-3"
    heading.textContent = "Proposed actions — nothing is saved until you commit"
    wrapper.appendChild(heading)

    const checkboxes = actions.map((action, index) => {
      const row = document.createElement("label")
      row.className = "flex items-start gap-2 py-1 text-sm text-gray-800"
      const box = document.createElement("input")
      box.type = "checkbox"
      box.checked = true
      box.dataset.index = index
      box.className = "mt-1 rounded border-gray-300 text-indigo-600"
      const label = document.createElement("span")
      label.innerHTML = `<span class="font-medium"></span> <span class="text-gray-500"></span>`
      label.querySelector(".font-medium").textContent = action.type
      label.querySelector(".text-gray-500").textContent =
        action.data.title || action.data.next_step || action.data.summary ||
        action.data.organization_name ||
        (action.data.score !== undefined ? `score ${action.data.score}` : "")
      row.append(box, label)
      wrapper.appendChild(row)
      return box
    })

    const commit = document.createElement("button")
    commit.type = "button"
    commit.className = "mt-3 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
    commit.textContent = "Commit selected"
    commit.addEventListener("click", async () => {
      const chosen = actions.filter((_, index) => checkboxes[index].checked)
      if (!chosen.length) {
        this.showError("Select at least one action to commit.")
        return
      }

      commit.disabled = true
      commit.textContent = "Committing…"
      try {
        const data = await this.postJson(this.commitUrlValue, { actions: chosen })
        wrapper.appendChild(this.commitSummary(data))
        commit.textContent = "Committed"
      } catch (error) {
        this.showError(error.message)
        commit.disabled = false
        commit.textContent = "Commit selected"
      }
    })
    wrapper.appendChild(commit)

    return wrapper
  }

  commitSummary(data) {
    const summary = document.createElement("div")
    summary.className = "mt-3 text-sm"
    const committed = data.committed || []
    const failed = data.failed || []

    const ok = document.createElement("p")
    ok.className = "text-emerald-700"
    ok.textContent = `Committed ${committed.length} action${committed.length === 1 ? "" : "s"}: ${committed.map((c) => c.type).join(", ")}`
    summary.appendChild(ok)

    if (failed.length) {
      const bad = document.createElement("p")
      bad.className = "text-red-700 mt-1"
      bad.textContent = `Failed: ${failed.map((f) => `${f.type} (${f.error})`).join("; ")}`
      summary.appendChild(bad)
    }

    return summary
  }

  // --- misc --------------------------------------------------------------

  setLoading(loading) {
    this.runButtonTarget.disabled = loading
    this.runButtonTarget.textContent = loading ? "Running…" : "Run agent"
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
